import { FormEvent, StrictMode, useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

type View = "overview" | "positions" | "strategies" | "calendar" | "admin";
type DataScope = "accounts" | "positions" | "orders" | "brokerages" | "market" | "equity" | "news" | "movers" | "pl" | "subscriptions" | "all";
type Theme = "light" | "dark";
type MarketSession = "rth" | "pre" | "post" | "closed";
type SessionProvider = () => Promise<SessionState>;

interface TenantMembership {
  id: string;
  display_name: string;
  role: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: { id: string; email: string };
  active_tenant: string;
  tenants: TenantMembership[];
}

interface SessionState extends LoginResponse {}

interface MarketStatus {
  session: MarketSession;
  label: string;
  detail: string;
  countdown: string;
  countdownLabel: "until open" | "until close";
}

interface AccountRow {
  id: string;
  display_name: string;
  account_type: string;
  trading_enabled: number;
  account_info_enabled: number;
  snapshot_json: string;
  updated_at: string;
}

interface PositionRow {
  id: string;
  account_id: string;
  canonical_symbol: string;
  projection_json: string;
  updated_at: string;
}

interface OrderRow {
  id: string;
  account_id: string;
  broker_order_id: string | null;
  status: string;
  order_json: string;
  created_at: string;
  updated_at: string;
}

interface BrokerageRow {
  id: string;
  broker_kind: string;
  display_name: string;
  status: string;
  environment?: "live" | "paper" | string;
  created_at: string;
  updated_at: string;
}

interface CreateBrokerageResponse {
  broker_connection: {
    id: string;
    broker_kind: string;
    display_name: string;
    status: string;
    environment?: "live" | "paper" | string;
    credentials_configured?: string[];
  };
}

interface MarketStripRow {
  symbol: string;
  price: number;
  change_percent: number;
  updated_at?: string;
  source?: string;
  feed?: string;
}

interface GatewayStreamStatus {
  stream_key?: string;
  stream_type?: "account" | "market_data" | string;
  broker_connection_id?: string;
  broker_kind?: string;
  environment?: string;
  feed?: string | null;
  symbols?: string[];
  status?: string;
  stream_names?: string[];
  connected_at?: string | null;
  last_message_at?: string | null;
  last_flush_at?: string | null;
  last_error?: string | null;
}

interface GatewayStatus {
  status?: string;
  runtime?: string;
  canonical?: boolean;
  streams?: GatewayStreamStatus[];
}

interface EquityPoint {
  timestamp: string;
  value: number;
}

interface EquitySummary {
  start_value: number;
  end_value: number;
  change_value: number;
  change_percent: number;
  max_drawdown_percent: number;
}

interface NewsRow {
  id: string;
  headline: string;
  symbols: string[];
  source?: string;
  published_at?: string;
}

interface MoverRow {
  symbol: string;
  name?: string;
  price: number;
  change_percent: number;
}

interface PlSummaryPeriod {
  label: "today" | "week" | "month" | "ytd" | string;
  pl: number;
  trades: number;
}

interface AppData {
  versions: Record<string, string>;
  accounts: AccountRow[];
  positions: PositionRow[];
  orders: OrderRow[];
  brokerages: BrokerageRow[];
  gatewayStatus: GatewayStatus | null;
  marketStrip: MarketStripRow[];
  equity: {
    range: string;
    points: EquityPoint[];
    summary: EquitySummary | null;
  };
  news: NewsRow[];
  movers: MoverRow[];
  pl: {
    days: CalendarDay[];
    summary: PlSummaryPeriod[];
  };
}

interface BootstrapResponse {
  versions: Record<string, string>;
  accounts: AccountRow[];
  positions: PositionRow[];
  orders: OrderRow[];
  brokerages: BrokerageRow[];
  gateway_status?: GatewayStatus | null;
  market_strip: MarketStripRow[];
  equity: {
    range: string;
    points: EquityPoint[];
    summary: EquitySummary | null;
  };
  news: NewsRow[];
  movers: MoverRow[];
  pl: {
    monthly: { days: CalendarDay[] };
    summary: PlSummaryPeriod[];
  };
}

interface RealtimeMessage {
  type?: string;
  scope?: DataScope;
  version?: string;
  payload?: unknown;
}

interface RealtimePayload {
  kind?: string;
  markets?: MarketStripRow[];
  news?: NewsRow[];
  gateway_status?: GatewayStatus;
}

interface PositionViewRow {
  id: string;
  symbol: string;
  account: string;
  strategy: string;
  status: string;
  legs: string;
  expiration: string;
  dte: number | null;
  quantity: number;
  mark: number;
  marketValue: number;
  exposure: number;
  dayPL: number;
  totalPL: number;
  totalPLPct: number;
}

interface CalendarDay {
  date: string;
  day: number;
  pl: number;
  trades: number;
  fees: number;
  alerts: string[];
}

const API_ORIGIN = import.meta.env.VITE_TRADERVIZ_API_ORIGIN ?? "";
const SESSION_KEY = "traderviz.session";
const THEME_KEY = "traderviz.theme";
const PRIVACY_KEY = "traderviz.privacy";
const TOKEN_REFRESH_SKEW_MS = 2 * 60 * 1000;
const TOKEN_REFRESH_MIN_DELAY_MS = 5 * 1000;
const REALTIME_HEARTBEAT_MS = 30_000;
const REALTIME_HEARTBEAT_TIMEOUT_MS = 90_000;
const REALTIME_RECONNECT_MAX_MS = 30_000;
const RANGE_TABS = ["1W", "1M", "3M", "6M", "YTD", "1Y", "ALL"] as const;
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const EMPTY_DATA: AppData = {
  versions: {},
  accounts: [],
  positions: [],
  orders: [],
  brokerages: [],
  gatewayStatus: null,
  marketStrip: [],
  equity: { range: "1M", points: [], summary: null },
  news: [],
  movers: [],
  pl: { days: [], summary: [] }
};

class ApiError extends Error {
  constructor(readonly path: string, readonly status: number) {
    super(`${path} failed with ${status}`);
  }
}

function readJson<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function jwtPayload(token: string): { exp?: number } | null {
  const encoded = token.split(".")[1];
  if (!encoded) return null;
  const normalized = encoded.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  try {
    return JSON.parse(atob(padded)) as { exp?: number };
  } catch {
    return null;
  }
}

function tokenExpiresAt(token: string): number | null {
  const exp = jwtPayload(token)?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
}

function tokenRefreshDelay(session: SessionState): number {
  const expiresAt = tokenExpiresAt(session.access_token);
  if (!expiresAt) return TOKEN_REFRESH_MIN_DELAY_MS;
  return Math.max(TOKEN_REFRESH_MIN_DELAY_MS, expiresAt - Date.now() - TOKEN_REFRESH_SKEW_MS);
}

function tokenNeedsRefresh(session: SessionState): boolean {
  const expiresAt = tokenExpiresAt(session.access_token);
  return !expiresAt || expiresAt - Date.now() <= TOKEN_REFRESH_SKEW_MS;
}

function money(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "--";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

function signedMoney(value: number): string {
  if (!Number.isFinite(value)) return "--";
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${money(value)}`;
}

function pct(value: number): string {
  if (!Number.isFinite(value)) return "";
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${value.toFixed(2)}%`;
}

function plClass(value: number): "mv-pos" | "mv-neg" | "mv-neu" {
  if (value > 0) return "mv-pos";
  if (value < 0) return "mv-neg";
  return "mv-neu";
}

function privateClass(className = ""): string {
  return `${className} private-value`.trim();
}

function navLabel(view: View): string {
  if (view === "calendar") return "P/L Calendar";
  if (view === "admin") return "Admin";
  return view;
}

function activeRole(session: SessionState): string {
  return session.tenants.find((tenant) => tenant.id === session.active_tenant)?.role ?? "member";
}

function canAdminTenant(session: SessionState): boolean {
  const role = activeRole(session);
  return role === "admin" || role === "owner";
}

function easternParts(date: Date): { year: number; month: number; day: number; weekday: string; minutes: number; time: string } {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
  const parts = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  const hour = Number(parts.hour === "24" ? "0" : parts.hour);
  const minute = Number(parts.minute);
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    weekday: parts.weekday ?? "Sun",
    minutes: hour * 60 + minute,
    time: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ET`
  };
}

function offsetMinutesAt(date: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
    hour: "2-digit"
  });
  const zone = formatter.formatToParts(date).find((part) => part.type === "timeZoneName")?.value ?? "GMT";
  const match = zone.match(/^GMT(?:(?<sign>[+-])(?<hours>\d{1,2})(?::(?<minutes>\d{2}))?)?$/);
  if (!match?.groups?.sign) return 0;
  const sign = match.groups.sign === "-" ? -1 : 1;
  const hours = Number(match.groups.hours ?? "0");
  const minutes = Number(match.groups.minutes ?? "0");
  return sign * (hours * 60 + minutes);
}

function easternWallTimeToDate(year: number, month: number, day: number, hour: number, minute: number): Date {
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const offset = offsetMinutesAt(utcGuess, "America/New_York");
  return new Date(Date.UTC(year, month - 1, day, hour, minute) - offset * 60_000);
}

function nextWeekday(year: number, month: number, day: number): { year: number; month: number; day: number } {
  let cursor = easternWallTimeToDate(year, month, day, 12, 0);
  do {
    cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);
    const parts = easternParts(cursor);
    if (!["Sat", "Sun"].includes(parts.weekday)) {
      return { year: parts.year, month: parts.month, day: parts.day };
    }
  } while (true);
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const clock = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}

function nextRthOpen(et: ReturnType<typeof easternParts>): Date {
  const weekdayOpen = !["Sat", "Sun"].includes(et.weekday);
  if (weekdayOpen && et.minutes < 9 * 60 + 30) {
    return easternWallTimeToDate(et.year, et.month, et.day, 9, 30);
  }
  const next = nextWeekday(et.year, et.month, et.day);
  return easternWallTimeToDate(next.year, next.month, next.day, 9, 30);
}

function marketSession(date = new Date()): MarketStatus {
  const et = easternParts(date);
  const weekdayOpen = !["Sat", "Sun"].includes(et.weekday);
  const rthOpen = weekdayOpen && et.minutes >= 9 * 60 + 30 && et.minutes < 16 * 60;
  const preMarket = weekdayOpen && et.minutes >= 4 * 60 && et.minutes < 9 * 60 + 30;
  const afterHours = weekdayOpen && et.minutes >= 16 * 60 && et.minutes < 20 * 60;

  if (rthOpen) {
    const close = easternWallTimeToDate(et.year, et.month, et.day, 16, 0);
    return { session: "rth", label: "RTH open", detail: et.time, countdown: formatCountdown(close.getTime() - date.getTime()), countdownLabel: "until close" };
  }

  const open = nextRthOpen(et);
  const countdown = formatCountdown(open.getTime() - date.getTime());
  if (preMarket) return { session: "pre", label: "Pre-market", detail: et.time, countdown, countdownLabel: "until open" };
  if (afterHours) return { session: "post", label: "After-hours", detail: et.time, countdown, countdownLabel: "until open" };
  return { session: "closed", label: "Closed", detail: et.time, countdown, countdownLabel: "until open" };
}

function pathForView(view: View): string {
  if (view === "positions") return "/positions";
  if (view === "strategies") return "/strategies";
  if (view === "calendar") return "/pl-calendar";
  if (view === "admin") return "/admin";
  return "/";
}

function viewForPath(pathname: string): View {
  if (pathname === "/positions") return "positions";
  if (pathname === "/strategies") return "strategies";
  if (pathname === "/pl-calendar" || pathname === "/calendar") return "calendar";
  if (pathname === "/admin" || pathname === "/settings") return "admin";
  return "overview";
}

function authHeaders(session: SessionState): HeadersInit {
  return { Authorization: `Bearer ${session.access_token}` };
}

async function apiGet<T>(path: string, session: SessionState): Promise<T> {
  const response = await fetch(`${API_ORIGIN}${path}`, { headers: authHeaders(session) });
  if (!response.ok) throw new ApiError(path, response.status);
  return response.json() as Promise<T>;
}

async function apiGetFresh<T>(path: string, getSession: SessionProvider): Promise<T> {
  return apiGet<T>(path, await getSession());
}

async function apiPost<T>(path: string, session: SessionState, body: unknown): Promise<T> {
  const response = await fetch(`${API_ORIGIN}${path}`, {
    method: "POST",
    headers: { ...authHeaders(session), "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(detail?.error ? `${path} failed: ${detail.error}` : `${path} failed with ${response.status}`);
  }
  return response.json() as Promise<T>;
}

async function refreshSession(session: SessionState): Promise<SessionState> {
  const response = await fetch(`${API_ORIGIN}/api/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: session.refresh_token })
  });
  if (!response.ok) throw new Error(`Refresh failed with ${response.status}`);
  return response.json() as Promise<SessionState>;
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null) as { error?: string } | null;
  return body?.error ? `${fallback}: ${body.error}` : fallback;
}

async function loginErrorMessage(response: Response): Promise<string> {
  const body = await response.json().catch(() => null) as { error?: string } | null;
  if (response.status === 401 || body?.error === "invalid_credentials") {
    return "Invalid email or password.";
  }
  if (response.status === 429 || body?.error === "login_rate_limited") {
    return "Too many login attempts. Please wait a few minutes and try again.";
  }
  if (body?.error === "email_and_password_required") {
    return "Enter your email and password.";
  }
  return "Sign in failed. Please try again.";
}

function realtimeUrl(): string {
  const base = API_ORIGIN || window.location.origin;
  const url = new URL("/api/realtime/connect", base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

function accountSnapshot(account: AccountRow): Record<string, unknown> {
  return readJson<Record<string, unknown>>(account.snapshot_json) ?? {};
}

function stringField(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

function numberField(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function boolLabel(value: unknown): string {
  return value === true ? "Yes" : value === false ? "No" : "--";
}

function accountTotals(data: AppData) {
  return data.accounts.reduce(
    (acc, account) => {
      const snapshot = accountSnapshot(account);
      const netLiquidation = typeof snapshot.net_liquidation === "number" ? snapshot.net_liquidation : 0;
      const cash = typeof snapshot.cash === "number" ? snapshot.cash : 0;
      const buyingPower = typeof snapshot.buying_power === "number" ? snapshot.buying_power : cash;
      const marginUsed = typeof snapshot.margin_used === "number" ? snapshot.margin_used : 0;
      const dayPL = typeof snapshot.day_pl === "number" ? snapshot.day_pl : 0;
      return {
        balance: acc.balance + netLiquidation,
        cash: acc.cash + cash,
        day: acc.day + dayPL,
        bp: acc.bp + buyingPower,
        margin: acc.margin + marginUsed
      };
    },
    { balance: 0, cash: 0, day: 0, bp: 0, margin: 0 }
  );
}

function derivePositions(data: AppData): PositionViewRow[] {
  const accountName = new Map(data.accounts.map((account) => [account.id, account.display_name]));
  const base: PositionViewRow[] = data.positions.map((position) => {
    const projection = readJson<Record<string, unknown>>(position.projection_json) ?? {};
    const quantity = numberField(projection.quantity);
    const marketValue = numberField(projection.market_value);
    const totalPL = numberField(projection.unrealized_pl);
    const dayPL = numberField(projection.day_pl);
    const mark = typeof projection.mark === "number" ? projection.mark : quantity ? marketValue / quantity : marketValue;
    const expiration = stringField(projection.expiration);
    const dte = typeof projection.dte === "number" ? projection.dte : null;
    const exposure = Math.max(
      0,
      numberField(projection.assignment_exposure),
      numberField(projection.strategy_exposure),
      numberField(projection.short_option_exposure)
    );
    return {
      id: position.id,
      symbol: position.canonical_symbol,
      account: accountName.get(position.account_id) ?? position.account_id,
      strategy: stringField(projection.strategy, position.canonical_symbol === "USD" ? "Cash" : "Position"),
      status: stringField(projection.status, "OPEN"),
      legs: stringField(projection.legs, position.canonical_symbol),
      expiration,
      dte,
      quantity,
      mark,
      marketValue,
      exposure,
      dayPL,
      totalPL,
      totalPLPct: marketValue ? (totalPL / Math.abs(marketValue)) * 100 : 0
    };
  });

  return base.sort((a, b) => (a.dte ?? 999) - (b.dte ?? 999) || a.symbol.localeCompare(b.symbol));
}

function Login({ onLogin }: { onLogin: (session: SessionState) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setIsBusy(true);
    setError(null);
    try {
      const response = await fetch(`${API_ORIGIN}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      if (!response.ok) throw new Error(await loginErrorMessage(response));
      const session = (await response.json()) as SessionState;
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      onLogin(session);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Login failed");
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel" aria-labelledby="title">
        <h1 id="title">TraderViz Console</h1>
        <form onSubmit={submit}>
          <label>
            <span>Email</span>
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required />
          </label>
          <label>
            <span>Password</span>
            <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" required />
          </label>
          {error ? <p className="login-error">{error}</p> : null}
          <button type="submit" disabled={isBusy}>{isBusy ? "Checking..." : "Continue"}</button>
        </form>
      </section>
    </main>
  );
}

function useDismissibleLayer<T extends HTMLElement>(isOpen: boolean, onDismiss: () => void) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: PointerEvent) {
      if (!ref.current?.contains(event.target as Node)) {
        onDismiss();
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onDismiss();
      }
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onDismiss]);

  return ref;
}

function streamHealthy(stream: GatewayStreamStatus): boolean {
  return stream.status ? ["connected", "healthy", "live"].includes(stream.status) : false;
}

function streamGroupOk(streams: GatewayStreamStatus[]): boolean {
  return streams.some(streamHealthy);
}

function streamSummary(streams: GatewayStreamStatus[], fallback: string): string {
  if (streams.length === 0) return fallback;
  return streams.map((stream) => {
    const broker = stringField(stream.broker_kind, "broker");
    const status = stringField(stream.status, "unknown");
    const feed = stream.stream_type === "market_data" && stream.feed ? ` ${stream.feed}` : "";
    const symbols = stream.symbols && stream.symbols.length > 0 ? ` ${stream.symbols.slice(0, 4).join(",")}` : "";
    return `${broker}${feed} ${status}${symbols}`;
  }).join(" · ");
}

function streamsFor(data: AppData, streamType: string): GatewayStreamStatus[] {
  return data.gatewayStatus?.streams?.filter((stream) => stream.stream_type === streamType) ?? [];
}

function hasConnectedStream(data: AppData, streamType: string): boolean {
  return streamsFor(data, streamType).some((stream) => stream.status === "connected");
}

function persistedMarketSummary(data: AppData): { value: string; meta: string } {
  const connected = hasConnectedStream(data, "market_data");
  if (data.marketStrip.length > 0) {
    const quoteCount = data.marketStrip.length;
    return {
      value: `${quoteCount} persisted quote${quoteCount === 1 ? "" : "s"}`,
      meta: connected ? "live stream connected" : "stored projection"
    };
  }
  return {
    value: "No stored market data",
    meta: connected ? "stream connected; waiting for first quote" : "stream not connected"
  };
}

function persistedNewsSummary(data: AppData): string {
  const connected = hasConnectedStream(data, "news");
  if (data.news.length > 0) return `${data.news.length} stored`;
  return connected ? "stream connected" : "no stored data";
}

function NewsSymbols({ symbols, fallback }: { symbols: string[]; fallback?: string }) {
  const cleanSymbols = symbols.map((symbol) => symbol.trim().toUpperCase()).filter(Boolean);
  if (cleanSymbols.length === 0) {
    return <span className="mv-news-source">{fallback || "market"}</span>;
  }
  return (
    <div className="mv-news-symbols" aria-label="Related symbols">
      {cleanSymbols.map((symbol) => (
        <button
          className="mv-news-symbol"
          key={symbol}
          type="button"
          title={`${symbol} details and buy action`}
          onClick={() => {
            window.dispatchEvent(new CustomEvent("traderviz:symbol-selected", { detail: { symbol, source: "news" } }));
          }}
        >
          {symbol}
        </button>
      ))}
    </div>
  );
}

function mergeMarketStrip(current: MarketStripRow[], updates: MarketStripRow[]): MarketStripRow[] {
  const bySymbol = new Map(current.map((row) => [row.symbol, row]));
  for (const update of updates) {
    if (!update.symbol) continue;
    bySymbol.set(update.symbol, { ...(bySymbol.get(update.symbol) ?? {}), ...update });
  }
  return [...bySymbol.values()].sort((left, right) => left.symbol.localeCompare(right.symbol));
}

function TopBar({
  view,
  setView,
  session,
  streamStatus,
  gatewayStatus,
  market,
  theme,
  privacyMode,
  onToggleTheme,
  onTogglePrivacy,
  onLogout
}: {
  view: View;
  setView: (view: View) => void;
  session: SessionState;
  streamStatus: string;
  gatewayStatus: GatewayStatus | null;
  market: MarketStatus;
  theme: Theme;
  privacyMode: boolean;
  onToggleTheme: () => void;
  onTogglePrivacy: () => void;
  onLogout: () => void;
}) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [subscriptionsOpen, setSubscriptionsOpen] = useState(false);
  const dismissProfile = useCallback(() => setProfileOpen(false), []);
  const dismissMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const dismissSubscriptions = useCallback(() => setSubscriptionsOpen(false), []);
  const profileRef = useDismissibleLayer<HTMLDivElement>(profileOpen, dismissProfile);
  const mobileNavRef = useDismissibleLayer<HTMLDivElement>(mobileNavOpen, dismissMobileNav);
  const subscriptionsRef = useDismissibleLayer<HTMLDivElement>(subscriptionsOpen, dismissSubscriptions);
  const activeTenant = session.tenants.find((tenant) => tenant.id === session.active_tenant);
  const navItems: View[] = ["overview", "positions", "strategies", "calendar", "admin"];
  const gatewayStreams = gatewayStatus?.streams ?? [];
  const brokerStreams = gatewayStreams.filter((stream) => stream.stream_type === "account");
  const marketStreams = gatewayStreams.filter((stream) => stream.stream_type === "market_data");
  const newsStreams = gatewayStreams.filter((stream) => stream.stream_type === "news");

  function selectView(nextView: View) {
    setView(nextView);
    setMobileNavOpen(false);
  }

  return (
    <header className="mv-topbar">
      <div className="mv-nav-layer" ref={mobileNavRef}>
        <button className="mv-menu-btn" aria-expanded={mobileNavOpen} aria-label="Open navigation" onClick={() => setMobileNavOpen((value) => !value)}>
          <span />
          <span />
          <span />
        </button>
        <nav className={`mv-mainnav ${mobileNavOpen ? "open" : ""}`}>
          {navItems.map((item) => (
            <button className={view === item ? "active" : ""} key={item} onClick={() => selectView(item)}>
              {navLabel(item)}
            </button>
          ))}
        </nav>
      </div>
      <button className="mv-brand" onClick={() => selectView("overview")}>
        <span className="mv-brand-mark" />
        <span>TraderViz</span>
      </button>
      <div className="mv-topbar-right">
        <div className="mv-subscription-menu-wrap" ref={subscriptionsRef}>
          <button className={`mv-status-chip mv-status-button ${streamStatus === "live" ? "live" : "reconnect"}`} aria-expanded={subscriptionsOpen} onClick={() => setSubscriptionsOpen((value) => !value)} title="View active data subscriptions">
            <span className="mv-dot" />
            {streamStatus}
          </button>
          {subscriptionsOpen ? (
            <section className="mv-subscription-menu" role="dialog" aria-labelledby="subscriptions-title">
              <div className="mv-subscription-hd">
                <div>
                  <h2 id="subscriptions-title">Data Subscriptions</h2>
                  <p>Active realtime feeds for this tenant session.</p>
                </div>
                <button className="mv-icon-btn" onClick={() => setSubscriptionsOpen(false)} title="Close">×</button>
              </div>
              <div className="mv-subscription-list">
                <div className="mv-subscription-row">
                  <span className={`mv-dot ${streamStatus === "live" ? "ok" : "warn"}`} />
                  <div>
                    <strong>TraderViz realtime channel</strong>
                    <span>{streamStatus === "live" ? "Connected to tenant updates" : "Not currently connected"}</span>
                  </div>
                </div>
                <div className={`mv-subscription-row ${brokerStreams.length > 0 ? "" : "muted"}`}>
                  <span className={`mv-dot ${streamGroupOk(brokerStreams) ? "ok" : brokerStreams.length > 0 ? "warn" : ""}`} />
                  <div>
                    <strong>Broker account streams</strong>
                    <span>{streamSummary(brokerStreams, "No active broker feed subscriptions reported.")}</span>
                  </div>
                </div>
                <div className={`mv-subscription-row ${marketStreams.length > 0 ? "" : "muted"}`}>
                  <span className={`mv-dot ${streamGroupOk(marketStreams) ? "ok" : marketStreams.length > 0 ? "warn" : ""}`} />
                  <div>
                    <strong>Market data streams</strong>
                    <span>{streamSummary(marketStreams, "No active market data streams reported.")}</span>
                  </div>
                </div>
                <div className={`mv-subscription-row ${newsStreams.length > 0 ? "" : "muted"}`}>
                  <span className={`mv-dot ${streamGroupOk(newsStreams) ? "ok" : newsStreams.length > 0 ? "warn" : ""}`} />
                  <div>
                    <strong>News streams</strong>
                    <span>{streamSummary(newsStreams, "No active news feed subscriptions reported.")}</span>
                  </div>
                </div>
                <div className="mv-subscription-row muted">
                  <span className="mv-dot" />
                  <div>
                    <strong>Strategy scanners</strong>
                    <span>No active strategy scanner subscriptions reported.</span>
                  </div>
                </div>
              </div>
            </section>
          ) : null}
        </div>
        <span className={`mv-status-chip market-${market.session}`} title={market.detail}>
          <span className="mv-dot" />
          <span className="mv-market-countdown">{market.countdown}</span>
          <span>{market.countdownLabel}</span>
        </span>
        <button className="mv-icon-btn" aria-pressed={privacyMode} title={privacyMode ? "Show private values" : "Hide private values"} onClick={onTogglePrivacy}>
          <span aria-hidden="true">{privacyMode ? "◉" : "◌"}</span>
          <span className="sr-only">{privacyMode ? "Show private values" : "Hide private values"}</span>
        </button>
        <button className="mv-icon-btn" aria-pressed={theme === "dark"} title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} onClick={onToggleTheme}>
          <span aria-hidden="true">{theme === "dark" ? "☀" : "☾"}</span>
          <span className="sr-only">{theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}</span>
        </button>
        <div className="mv-profile" ref={profileRef}>
          <button className="mv-profile-btn" aria-expanded={profileOpen} onClick={() => setProfileOpen((value) => !value)}>
            <span className="mv-avatar">{session.user.email.slice(0, 1).toUpperCase()}</span>
            <span className="mv-profile-email private-value">{session.user.email}</span>
          </button>
          {profileOpen ? (
            <div className="mv-profile-menu">
              <div className="mv-profile-meta">
                <div className="k">Signed in</div>
                <div className="v private-value">{session.user.email}</div>
              </div>
              <div className="mv-profile-meta">
                <div className="k">Tenant</div>
                <div className="v">{activeTenant?.display_name ?? session.active_tenant}</div>
                <div className="sub">{activeTenant?.role ?? "member"}</div>
              </div>
              <button onClick={onLogout}>Sign out</button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function OverviewPage({ data }: { data: AppData }) {
  const positionRows = derivePositions(data);
  const accountPortfolio = accountTotals(data);
  const exposure = positionRows.reduce((sum, row) => sum + row.exposure, 0);
  const portfolio = { ...accountPortfolio, exposure };
  const dayPct = portfolio.balance ? (portfolio.day / portfolio.balance) * 100 : 0;
  const [range, setRange] = useState<typeof RANGE_TABS[number]>("1M");
  const [selectedAccount, setSelectedAccount] = useState<AccountRow | null>(null);
  const equitySummary = data.equity.summary;
  const expiringRows = positionRows.filter((row) => row.dte !== null).slice(0, 5);
  const marketSummary = persistedMarketSummary(data);

  return (
    <div className="mv-page-pad">
      <div className="mv-market-strip">
        {data.marketStrip.length > 0 ? (
          data.marketStrip.map((market) => (
            <div className="mv-market-item" key={market.symbol}>
              <div>
                <div className="lbl">{market.symbol}</div>
                <div className="val">{market.price.toLocaleString("en-US", { maximumFractionDigits: 2 })}</div>
              </div>
              <div className={`chg ${plClass(market.change_percent)}`}>{pct(market.change_percent)}</div>
            </div>
          ))
        ) : (
          <div className="mv-market-item empty-strip">
            <div>
              <div className="lbl">Market Data</div>
              <div className="val">{marketSummary.value}</div>
              <div className="sub">{marketSummary.meta}</div>
            </div>
            <div className="chg mv-neu">--</div>
          </div>
        )}
      </div>

      <div className="mv-overview-grid">
        <div className="mv-overview-main">
          <section className="mv-hero">
            <div className="mv-hero-primary">
              <div className="mv-hero-label">Total Portfolio Value</div>
              <div className="mv-hero-value private-value">{money(portfolio.balance)}</div>
              <div className="mv-hero-sub">
                <span className={privateClass(`mv-hero-delta ${plClass(portfolio.day)}`)}>{signedMoney(portfolio.day)}</span>
                <span className={privateClass(plClass(dayPct))}>{pct(dayPct)}</span>
                <span>today</span>
              </div>
            </div>
            <div className="mv-hero-metrics">
              <HeroMetric k="Cash" v={money(portfolio.cash)} vsub="across tenant" />
              <HeroMetric k="Buying Power" v={money(portfolio.bp)} vsub="estimated" />
              <HeroMetric k="Exposure" v={money(portfolio.exposure)} vsub={`${portfolio.balance ? ((portfolio.exposure / portfolio.balance) * 100).toFixed(0) : "0"}% of NLV`} />
              <HeroMetric k="Margin Used" v={money(portfolio.margin)} vsub={`${portfolio.bp ? ((portfolio.margin / portfolio.bp) * 100).toFixed(1) : "0"}% util.`} />
            </div>
          </section>

          <section className="mv-card mv-equity-wrap">
            <div className="mv-equity-head">
              <div>
                <h3>Equity Curve</h3>
                <div className="sub">account history</div>
              </div>
              <div className="mv-range-tabs">
                {RANGE_TABS.map((tab) => (
                  <button className={range === tab ? "active" : ""} key={tab} onClick={() => setRange(tab)}>{tab}</button>
                ))}
              </div>
            </div>
            <div className="mv-equity-body">
              {data.equity.points.length >= 2 ? (
                <EquityChart points={data.equity.points} />
              ) : (
                <EmptyState title="No equity history" detail="The chart will populate after account snapshots are persisted by broker or reconciliation jobs." />
              )}
            </div>
            <div className="mv-equity-footer">
              <Metric k="Start" v={money(equitySummary?.start_value)} vsub={range} />
              <Metric k="End" v={money(equitySummary?.end_value)} vsub="latest" />
              <Metric
                k="Change"
                v={equitySummary ? signedMoney(equitySummary.change_value) : "--"}
                vsub={equitySummary ? pct(equitySummary.change_percent) : "not available"}
                tone={equitySummary && equitySummary.change_value >= 0 ? "pos" : "neg"}
              />
              <Metric k="Max DD" v={equitySummary ? pct(equitySummary.max_drawdown_percent) : "--"} vsub="realized" tone="neg" />
            </div>
          </section>
        </div>

        <aside className="mv-overview-side">
          <section className="mv-card">
            <div className="mv-card-hd">
              <h3>Accounts</h3>
              <span className="mv-sub">{data.accounts.length}</span>
            </div>
            <div className="mv-accts">
              <div className="mv-acct-section-hd">
                <span>Live / Paper</span>
                <span>{data.brokerages.length} links</span>
              </div>
              {data.accounts.map((account, index) => {
                const snapshot = accountSnapshot(account);
                const bal = typeof snapshot.net_liquidation === "number" ? snapshot.net_liquidation : 0;
                const day = typeof snapshot.day_pl === "number" ? snapshot.day_pl : 0;
                return (
                  <div
                    className="mv-acct"
                    key={account.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedAccount(account)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedAccount(account);
                      }
                    }}
                  >
                    <span className={`mv-acct-dot a${(index % 4) + 1}`} />
                    <div className="name private-value">{account.display_name}</div>
                    <div className="bal private-value">{money(bal)}</div>
                    <div className={privateClass(`delta ${plClass(day)}`)}>{signedMoney(day)} <span className="pct">({pct(bal ? (day / bal) * 100 : 0)})</span></div>
                  </div>
                );
              })}
              {data.accounts.length === 0 ? <EmptyState title="No accounts" detail="Connect a broker account before account balances can render." compact /> : null}
            </div>
          </section>

          <section className="mv-card mv-exp-card">
            <div className="mv-card-hd">
              <h3>Upcoming Expirations</h3>
              <span className="mv-sub">strategy risk</span>
            </div>
            <div className="mv-exp-list">
              {expiringRows.map((row) => (
                <div className="mv-exp-row" key={row.id}>
                  <div className="mv-exp-date">
                    <div className="d">{row.expiration.slice(-2)}</div>
                    <div className="m">{MONTH_NAMES[Number(row.expiration.slice(5, 7)) - 1]?.slice(0, 3) ?? "--"}</div>
                  </div>
                  <div>
                    <div className="sym">{row.symbol}</div>
                    <div className="strat">{row.strategy}</div>
                  </div>
                  <div>
                    <span className={`mv-badge ${row.dte !== null && row.dte <= 2 ? "red" : row.dte !== null && row.dte <= 14 ? "amber" : "ghost"}`}>{row.dte} DTE</span>
                  </div>
                </div>
              ))}
              {expiringRows.length === 0 ? <EmptyState title="No expirations" detail="No option expiration metadata is available for current positions." compact /> : null}
            </div>
          </section>
        </aside>
      </div>

      <div className="mv-lower-grid">
        <section className="mv-card">
          <div className="mv-card-hd">
            <h3>Recent News</h3>
            <span className="mv-sub">{persistedNewsSummary(data)}</span>
          </div>
          {data.news.map((item) => (
            <div className="mv-news-row" key={item.id}>
              <div className="mv-news-copy">
                <div className="headline">{item.headline}</div>
                <NewsSymbols symbols={item.symbols} fallback={item.source} />
              </div>
              <div className="meta">{item.published_at ? new Date(item.published_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--"}</div>
            </div>
          ))}
          {data.news.length === 0 ? <EmptyState title="No stored news" detail="News will appear after the gateway persists a canonical news event." compact /> : null}
        </section>
        <section className="mv-card">
          <div className="mv-card-hd">
            <h3>Top Movers Today</h3>
            <span className="mv-sub">not persisted</span>
          </div>
          {data.movers.map((mover) => (
            <div className="mv-watch-row" key={mover.symbol}>
              <div>
                <div className="sym">{mover.symbol}</div>
                <div className="name">{mover.name ?? "market"}</div>
              </div>
              <div className="px">{mover.price.toFixed(2)}</div>
              <div className={`chg ${plClass(mover.change_percent)}`}>{pct(mover.change_percent)}</div>
            </div>
          ))}
          {data.movers.length === 0 ? <EmptyState title="No mover rankings" detail="Mover rankings will appear after a mover projection source persists them." compact /> : null}
        </section>
      </div>

      {selectedAccount ? (
        <AccountDetailModal
          account={selectedAccount}
          exposure={positionRows.filter((row) => row.account === selectedAccount.display_name || row.account === selectedAccount.id).reduce((sum, row) => sum + row.exposure, 0)}
          onClose={() => setSelectedAccount(null)}
        />
      ) : null}
    </div>
  );
}

function AccountDetailModal({ account, exposure, onClose }: { account: AccountRow; exposure: number; onClose: () => void }) {
  const snapshot = accountSnapshot(account);
  const status = stringField(snapshot.status, "unknown");
  const environment = stringField(snapshot.environment, account.account_type);
  const source = stringField(snapshot.source, "broker");
  const dayPL = numberField(snapshot.day_pl);
  const netLiquidation = numberField(snapshot.net_liquidation);
  const dayPct = netLiquidation ? (dayPL / netLiquidation) * 100 : 0;
  const primaryFields = [
    { label: "Net liquidation", value: money(netLiquidation) },
    { label: "Day P/L", value: `${signedMoney(dayPL)} (${pct(dayPct)})`, tone: plClass(dayPL) },
    { label: "Cash", value: money(snapshot.cash) },
    { label: "Buying power", value: money(snapshot.buying_power) },
    { label: "Exposure", value: money(exposure), tone: exposure > 0 ? "mv-neg" : "mv-neu" },
    { label: "Margin used", value: money(snapshot.margin_used), tone: numberField(snapshot.margin_used) > 0 ? "mv-neg" : "mv-neu" }
  ];
  const detailFields = [
    { label: "Currency", value: stringField(snapshot.currency, "USD") },
    { label: "Day trades", value: String(numberField(snapshot.daytrade_count)) },
    { label: "Pattern day trader", value: boolLabel(snapshot.pattern_day_trader) },
    { label: "Trading blocked", value: boolLabel(snapshot.trading_blocked) },
    { label: "Account blocked", value: boolLabel(snapshot.account_blocked) },
    { label: "Transfers blocked", value: boolLabel(snapshot.transfers_blocked) },
    { label: "Trade suspended", value: boolLabel(snapshot.trade_suspended_by_user) },
    { label: "Last updated", value: stringField(snapshot.updated_at) ? new Date(stringField(snapshot.updated_at)).toLocaleString() : "--" }
  ];
  const blocked = snapshot.account_blocked || snapshot.trading_blocked || snapshot.transfers_blocked || snapshot.trade_suspended_by_user;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="mv-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="mv-modal mv-account-modal" role="dialog" aria-modal="true" aria-labelledby="account-detail-title">
        <div className="mv-modal-hd">
          <div>
            <h3 id="account-detail-title">{account.display_name}</h3>
            <div className="mv-modal-sub">{source} · {environment} · {status}</div>
          </div>
          <button className="mv-modal-close" type="button" onClick={onClose} aria-label="Close account details">×</button>
        </div>
        {blocked ? <div className="mv-modal-alert">Account has one or more broker restrictions.</div> : null}
        <div className="mv-account-detail-grid">
          {primaryFields.map((field) => (
            <div className="mv-detail-row" key={field.label}>
              <span>{field.label}</span>
              <strong className={privateClass(field.tone ?? "")}>{field.value}</strong>
            </div>
          ))}
        </div>
        <div className="mv-modal-separator" />
        <div className="mv-account-detail-grid">
          {detailFields.map((field) => (
            <div className="mv-detail-row" key={field.label}>
              <span>{field.label}</span>
              <strong className={field.label === "Last updated" ? "" : "private-value"}>{field.value}</strong>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function EquityChart({ points }: { points: EquityPoint[] }) {
  const values = points.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const width = 720;
  const height = 220;
  const path = points.map((point, index) => {
    const x = points.length === 1 ? 0 : (index / (points.length - 1)) * width;
    const y = height - ((point.value - min) / span) * (height - 28) - 14;
    return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");

  return (
    <svg className="mv-equity-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Equity curve">
      <defs>
        <linearGradient id="eqFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--blue)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--blue)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[40, 85, 130, 175].map((y) => <line x1="0" x2={width} y1={y} y2={y} key={y} className="gridline" />)}
      <path className="line" d={path} />
    </svg>
  );
}

function EmptyState({ title, detail, compact = false }: { title: string; detail: string; compact?: boolean }) {
  return (
    <div className={`mv-empty-state ${compact ? "compact" : ""}`}>
      <div className="title">{title}</div>
      <div className="detail">{detail}</div>
    </div>
  );
}

function HeroMetric({ k, v, vsub }: { k: string; v: string; vsub: string }) {
  return (
    <div className="mv-hero-metric">
      <div className="k">{k}</div>
      <div className="v private-value">{v}</div>
      <div className="vsub">{vsub}</div>
    </div>
  );
}

function Metric({ k, v, vsub, tone }: { k: string; v: string; vsub: string; tone?: "pos" | "neg" }) {
  return (
    <div>
      <div className="k">{k}</div>
      <div className={privateClass(`v ${tone === "pos" ? "mv-pos" : tone === "neg" ? "mv-neg" : ""}`)}>{v}</div>
      <div className="vsub">{vsub}</div>
    </div>
  );
}

function PositionsPage({ data }: { data: AppData }) {
  const rows = derivePositions(data);
  const aggCurrent = rows.reduce((sum, row) => sum + row.dayPL, 0);
  const aggNet = rows.reduce((sum, row) => sum + row.totalPL, 0);
  const exposure = rows.reduce((sum, row) => sum + row.exposure, 0);

  return (
    <div className="mv-page-pad">
      <div className="mv-section-hd">
        <h2>Positions</h2>
        <span className="hint">Backend-owned projection view, grouped for strategy workflows.</span>
      </div>

      <div className="mv-perf-ribbon positions-ribbon">
        <Metric k="Open Positions" v={String(rows.length)} vsub="tenant scoped" />
        <Metric k="Exposure" v={money(exposure)} vsub="short options / strategies" />
        <Metric k="Current P/L" v={signedMoney(aggCurrent)} vsub="today" tone={aggCurrent >= 0 ? "pos" : "neg"} />
        <Metric k="Net P/L" v={signedMoney(aggNet)} vsub="unrealized" tone={aggNet >= 0 ? "pos" : "neg"} />
      </div>

      <section className="mv-card">
        {rows.length > 0 ? (
          <div className="mv-scroll-x">
          <table className="mv-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Strategy</th>
                <th>Status</th>
                <th>Legs</th>
                <th>Exp</th>
                <th className="num">DTE</th>
                <th className="num">Qty</th>
                <th className="num">Mark</th>
                <th className="num">Market Value</th>
                <th className="num">Day P/L</th>
                <th className="num">Net P/L</th>
                <th className="num">Net %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="strong private-value">{row.symbol}</td>
                  <td><span className="mv-tag blue">{row.strategy}</span></td>
                  <td><span className={`mv-badge ${row.status === "OPEN" ? "green" : "amber"}`}>{row.status}</span></td>
                  <td className="legs private-value">{row.legs}</td>
                  <td>{row.expiration || "--"}</td>
                  <td className={`num ${row.dte !== null && row.dte <= 2 ? "mv-neg" : row.dte !== null && row.dte <= 14 ? "mv-warn" : ""}`}>{row.dte ?? "--"}</td>
                  <td className="num private-value">{row.quantity}</td>
                  <td className="num private-value">{money(row.mark)}</td>
                  <td className="num private-value">{money(row.marketValue)}</td>
                  <td className={privateClass(`num ${plClass(row.dayPL)}`)}>{signedMoney(row.dayPL)}</td>
                  <td className={privateClass(`num ${plClass(row.totalPL)}`)}>{signedMoney(row.totalPL)}</td>
                  <td className={privateClass(`num ${plClass(row.totalPLPct)}`)}>{pct(row.totalPLPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <EmptyState title="No open positions" detail="Positions will appear after broker snapshots or reconciliation jobs persist tenant-scoped projections." />
        )}
      </section>
    </div>
  );
}

function StrategiesPage() {
  return (
    <div className="mv-page-pad">
      <div className="mv-section-hd">
        <h2>Strategies</h2>
        <span className="hint">Opportunity discovery, execution review, and active strategy tracking.</span>
      </div>

      <div className="mv-strategy-grid">
        <section className="mv-card mv-strategy-primary">
          <div className="mv-card-hd">
            <h3>Opportunities</h3>
            <span className="mv-sub">strategy engine</span>
          </div>
          <EmptyState
            title="No strategy opportunities"
            detail="Opportunities will appear after strategy scanners are connected to broker projections, market data, and guardrail checks."
          />
        </section>

        <section className="mv-card">
          <div className="mv-card-hd">
            <h3>Active Strategies</h3>
            <span className="mv-sub">0 active</span>
          </div>
          <EmptyState
            title="No active strategies"
            detail="Accepted strategies will track lifecycle state, risk, broker order status, and exit criteria here."
            compact
          />
        </section>

        <section className="mv-card">
          <div className="mv-card-hd">
            <h3>Strategy Activity</h3>
            <span className="mv-sub">audit trail</span>
          </div>
          <EmptyState
            title="No strategy activity"
            detail="Scanner decisions, user approvals, order intents, and strategy state changes will render after the strategy backend is enabled."
            compact
          />
        </section>
      </div>
    </div>
  );
}

function AdminPage({
  data,
  getSession,
  session,
  onBrokerageCreated
}: {
  data: AppData;
  getSession: SessionProvider;
  session: SessionState;
  onBrokerageCreated: (brokerage: BrokerageRow) => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [brokerKind, setBrokerKind] = useState<"schwab" | "alpaca" | "tradestation">("alpaca");
  const [environment, setEnvironment] = useState<"paper" | "live">("paper");
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminNotice, setAdminNotice] = useState<string | null>(null);
  const isAdmin = canAdminTenant(session);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!isAdmin) return;
    setIsSaving(true);
    setAdminError(null);
    setAdminNotice(null);
    try {
      const result = await apiPost<CreateBrokerageResponse>("/api/brokerages", await getSession(), {
        broker_kind: brokerKind,
        display_name: displayName,
        environment,
        credentials: {
          access_key: accessKey,
          secret_key: secretKey
        }
      });
      const brokerConnection = result.broker_connection;
      onBrokerageCreated({
        id: brokerConnection.id,
        broker_kind: brokerConnection.broker_kind,
        display_name: brokerConnection.display_name,
        status: brokerConnection.status,
        environment: brokerConnection.environment ?? environment,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      setDisplayName("");
      setAccessKey("");
      setSecretKey("");
      setAdminNotice("Brokerage credentials stored. Secrets are write-only and cannot be viewed again.");
    } catch (caught) {
      setAdminError(caught instanceof Error ? caught.message : "Brokerage setup failed");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="mv-page-pad">
      <div className="mv-section-hd">
        <h2>Admin</h2>
        <span className="hint">Tenant-scoped brokerage configuration and secure credential onboarding.</span>
      </div>

      {!isAdmin ? (
        <section className="mv-card mv-admin-restricted">
          <EmptyState
            title="Tenant admin required"
            detail="Brokerage credentials can only be managed by a tenant administrator."
          />
        </section>
      ) : (
        <div className="mv-admin-grid">
          <section className="mv-card mv-admin-form-card">
            <div className="mv-card-hd">
              <h3>Add Brokerage</h3>
              <span className="mv-sub">write-only secrets</span>
            </div>
            <form className="mv-admin-form" onSubmit={submit}>
              <label>
                <span>Brokerage name</span>
                <input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Primary Paper Account"
                  required
                  maxLength={80}
                />
              </label>

              <div className="mv-form-row">
                <label>
                  <span>Provider</span>
                  <select value={brokerKind} onChange={(event) => setBrokerKind(event.target.value as typeof brokerKind)}>
                    <option value="alpaca">Alpaca</option>
                    <option value="schwab">Schwab</option>
                    <option value="tradestation">TradeStation</option>
                  </select>
                </label>
                <label>
                  <span>Type</span>
                  <select value={environment} onChange={(event) => setEnvironment(event.target.value as typeof environment)}>
                    <option value="paper">Paper</option>
                    <option value="live">Live</option>
                  </select>
                </label>
              </div>

              {environment === "live" ? (
                <div className="mv-live-warning">
                  Live brokerages are accepted for storage only. Trading submission remains gated by backend guardrails and adapter readiness.
                </div>
              ) : null}

              <label>
                <span>Access key</span>
                <input
                  className="private-value"
                  value={accessKey}
                  onChange={(event) => setAccessKey(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  required
                />
              </label>
              <label>
                <span>Secret key</span>
                <input
                  className="private-value"
                  value={secretKey}
                  onChange={(event) => setSecretKey(event.target.value)}
                  type="password"
                  autoComplete="new-password"
                  spellCheck={false}
                  required
                />
              </label>

              {adminError ? <div className="mv-inline-error">{adminError}</div> : null}
              {adminNotice ? <div className="mv-inline-success">{adminNotice}</div> : null}

              <div className="mv-admin-actions">
                <button className="mv-btn primary" type="submit" disabled={isSaving}>
                  {isSaving ? "Storing..." : "Store brokerage"}
                </button>
              </div>
            </form>
          </section>

          <section className="mv-card">
            <div className="mv-card-hd">
              <h3>Configured Brokerages</h3>
              <span className="mv-sub">{data.brokerages.length} links</span>
            </div>
            <div className="mv-broker-list">
              {data.brokerages.map((brokerage) => (
                <div className="mv-broker-row" key={brokerage.id}>
                  <div>
                    <div className="mv-broker-title">{brokerage.display_name}</div>
                    <div className="mv-broker-meta">
                      <span>{brokerage.broker_kind}</span>
                      <span>{brokerage.environment ?? "paper"}</span>
                      <span>{brokerage.status}</span>
                    </div>
                  </div>
                  <div className="mv-broker-secret-state">
                    <span className="mv-badge green">configured</span>
                    <span>secrets hidden</span>
                  </div>
                </div>
              ))}
              {data.brokerages.length === 0 ? (
                <EmptyState
                  title="No brokerages configured"
                  detail="Add a paper or live brokerage link to enable future REST fallback and market-hour gateway subscriptions."
                  compact
                />
              ) : null}
            </div>
          </section>

          <section className="mv-card mv-admin-notes">
            <div className="mv-card-hd">
              <h3>Security Notes</h3>
              <span className="mv-sub">credential policy</span>
            </div>
            <div className="mv-note-list">
              <div>Credentials are sent only to the authenticated API and broker boundary.</div>
              <div>D1 stores ciphertext and metadata; Secrets Store holds the root key material.</div>
              <div>Broker-native payloads and decrypted secrets are never sent to browsers or mobile clients.</div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function summaryFor(summary: PlSummaryPeriod[], label: string): PlSummaryPeriod {
  return summary.find((item) => item.label === label) ?? { label, pl: 0, trades: 0 };
}

function CalendarPage({ getSession, initialDays, summary }: { getSession: SessionProvider; initialDays: CalendarDay[]; summary: PlSummaryPeriod[] }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [instrument, setInstrument] = useState<"all" | "options" | "equities">("all");
  const [days, setDays] = useState<CalendarDay[]>(initialDays);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const total = days.reduce((sum, day) => sum + day.pl, 0);
  const trades = days.reduce((sum, day) => sum + day.trades, 0);
  const today = summaryFor(summary, "today");
  const week = summaryFor(summary, "week");
  const ytd = summaryFor(summary, "ytd");

  useEffect(() => {
    setDays(initialDays);
  }, [initialDays]);

  useEffect(() => {
    let active = true;
    setCalendarError(null);
    apiGetFresh<{ days: CalendarDay[] }>(`/api/pl/monthly?year=${year}&month=${month}&instrument=${instrument}`, getSession)
      .then((result) => {
        if (active) setDays(result.days);
      })
      .catch((caught) => {
        if (active) setCalendarError(caught instanceof Error ? caught.message : "P/L calendar refresh failed");
      });
    return () => {
      active = false;
    };
  }, [year, month, instrument, getSession]);

  function prevMonth() {
    if (month === 1) {
      setYear((value) => value - 1);
      setMonth(12);
    } else {
      setMonth((value) => value - 1);
    }
  }

  function nextMonth() {
    if (month === 12) {
      setYear((value) => value + 1);
      setMonth(1);
    } else {
      setMonth((value) => value + 1);
    }
  }

  return (
    <div className="mv-page-pad">
      <div className="mv-section-hd">
        <h2>P/L Calendar</h2>
        <span className="hint">Weekday realized P/L view from tenant-scoped backend records.</span>
      </div>

      <div className="mv-toolbar">
        {(["all", "options", "equities"] as const).map((item) => (
          <button className={`mv-pill ${instrument === item ? "active" : ""}`} key={item} onClick={() => setInstrument(item)}>
            {item}
          </button>
        ))}
      </div>

      <section className="mv-card summary-card">
        {calendarError ? <div className="mv-inline-error">{calendarError}</div> : null}
        <table className="mv-summary-table">
          <thead>
            <tr>
              <th></th>
              <th>Today</th>
              <th>Week</th>
              <th>Month</th>
              <th>YTD</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>P/L</td>
              <td className={privateClass(plClass(today.pl))}>{signedMoney(today.pl)}</td>
              <td className={privateClass(plClass(week.pl))}>{signedMoney(week.pl)}</td>
              <td className={privateClass(plClass(total))}>{signedMoney(total)}</td>
              <td className={privateClass(plClass(ytd.pl))}>{signedMoney(ytd.pl)}</td>
            </tr>
            <tr>
              <td>Trades</td>
              <td>{today.trades}</td>
              <td>{week.trades}</td>
              <td>{trades}</td>
              <td>{ytd.trades}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="calendar-shell">
        <div className="calendar-head">
          <button className="mv-btn ghost" onClick={prevMonth}>◀</button>
          <strong>{MONTH_NAMES[month - 1]} {year}</strong>
          <button className="mv-btn ghost" onClick={nextMonth}>▶</button>
          <span className={privateClass(`calendar-total ${plClass(total)}`)}>Net: {signedMoney(total)}</span>
        </div>
        <div className="mv-calendar-grid">
          {["Mon", "Tue", "Wed", "Thu", "Fri"].map((day) => <div className="mv-cal-head" key={day}>{day}</div>)}
          {buildCalendarGrid(year, month, days).flatMap((week, weekIndex) =>
            week.map((cell, dayIndex) => (
              cell ? <CalendarCell key={`${weekIndex}-${dayIndex}`} cell={cell} year={year} month={month} /> : <div className="mv-cal-cell empty" key={`${weekIndex}-${dayIndex}`} />
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function buildCalendarGrid(year: number, month: number, days: CalendarDay[]): (CalendarDay | null)[][] {
  const dayMap = new Map(days.map((day) => [day.day, day]));
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0).getDate();
  const weeks: (CalendarDay | null)[][] = [];
  let week: (CalendarDay | null)[] = [];
  let startDow = first.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;
  for (let i = 0; i < Math.min(startDow, 5); i += 1) week.push(null);
  for (let day = 1; day <= last; day += 1) {
    const dow = new Date(year, month - 1, day).getDay();
    if (dow === 0 || dow === 6) continue;
    week.push(dayMap.get(day) ?? {
      date: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      day,
      pl: 0,
      trades: 0,
      fees: 0,
      alerts: []
    });
    if (week.length === 5) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length) {
    while (week.length < 5) week.push(null);
    weeks.push(week);
  }
  return weeks;
}

function CalendarCell({ cell, year, month }: { cell: CalendarDay; year: number; month: number }) {
  const today = new Date();
  const isToday = today.getFullYear() === year && today.getMonth() + 1 === month && today.getDate() === cell.day;
  const tone = cell.trades === 0 ? "" : cell.pl > 0 ? "gain" : "loss";
  const strong = Math.abs(cell.pl) > 300 ? "strong" : "";
  return (
    <button className={`mv-cal-cell ${tone} ${strong} ${isToday ? "today" : ""}`}>
      <div className="day">{cell.day}</div>
      {cell.trades > 0 ? (
        <>
          <div className={privateClass(`pnl ${plClass(cell.pl)}`)}>{signedMoney(cell.pl)}</div>
          <div className="pcount private-value">{cell.trades} pos / ${cell.fees.toFixed(2)}</div>
        </>
      ) : (
        <div className="pnl muted">--</div>
      )}
      {cell.alerts.length > 0 ? <div className="alert-badge">⚠ {cell.alerts.join(", ")}</div> : null}
    </button>
  );
}

function Console({ session, onSession, onLogout }: { session: SessionState; onSession: (session: SessionState) => void; onLogout: () => void }) {
  const [view, setView] = useState<View>(() => viewForPath(window.location.pathname));
  const [data, setData] = useState<AppData>(EMPTY_DATA);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [streamStatus, setStreamStatus] = useState("connecting");
  const [theme, setTheme] = useState<Theme>(() => localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light");
  const [privacyMode, setPrivacyMode] = useState(() => localStorage.getItem(PRIVACY_KEY) === "true");
  const [market, setMarket] = useState(() => marketSession());
  const versionsRef = useRef<Record<string, string>>(data.versions);
  const sessionRef = useRef(session);
  const refreshPromiseRef = useRef<Promise<SessionState> | null>(null);
  const realtimeReconnectAttemptRef = useRef(0);

  useEffect(() => {
    versionsRef.current = data.versions;
  }, [data.versions]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(PRIVACY_KEY, String(privacyMode));
  }, [privacyMode]);

  useEffect(() => {
    const id = window.setInterval(() => setMarket(marketSession()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onPopState = () => setView(viewForPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function navigate(nextView: View) {
    setView(nextView);
    const nextPath = pathForView(nextView);
    if (window.location.pathname !== nextPath) {
      window.history.pushState(null, "", nextPath);
    }
  }

  const ensureFreshSession = useCallback(async (force = false): Promise<SessionState> => {
    const current = sessionRef.current;
    if (!force && !tokenNeedsRefresh(current)) {
      return current;
    }
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = refreshSession(current)
        .then((nextSession) => {
          sessionRef.current = nextSession;
          localStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
          onSession(nextSession);
          return nextSession;
        })
        .catch((caught) => {
          onLogout();
          throw caught;
        })
        .finally(() => {
          refreshPromiseRef.current = null;
        });
    }
    return refreshPromiseRef.current;
  }, [onLogout, onSession]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      void ensureFreshSession(true).catch(() => undefined);
    }, tokenRefreshDelay(session));
    return () => window.clearTimeout(id);
  }, [ensureFreshSession, session]);

  async function loadData(activeSession: SessionState) {
    const bootstrap = await apiGet<BootstrapResponse>("/api/bootstrap", activeSession);
    setData({
      versions: bootstrap.versions,
      accounts: bootstrap.accounts,
      positions: bootstrap.positions,
      orders: bootstrap.orders,
      brokerages: bootstrap.brokerages,
      gatewayStatus: bootstrap.gateway_status ?? null,
      marketStrip: bootstrap.market_strip,
      equity: bootstrap.equity,
      news: bootstrap.news,
      movers: bootstrap.movers,
      pl: {
        days: bootstrap.pl.monthly.days,
        summary: bootstrap.pl.summary
      }
    });
  }

  async function refreshScope(scope: DataScope, version?: string) {
    const activeSession = await ensureFreshSession();
    if (scope === "all") {
      await loadData(activeSession);
      return;
    }
    if (version && versionsRef.current[scope] === version) {
      return;
    }

    if (scope === "accounts") {
      const result = await apiGet<{ accounts: AccountRow[] }>("/api/accounts", activeSession);
      setData((current) => ({ ...current, accounts: result.accounts, versions: { ...current.versions, accounts: version ?? current.versions.accounts ?? "" } }));
    } else if (scope === "positions") {
      const result = await apiGet<{ positions: PositionRow[] }>("/api/positions", activeSession);
      setData((current) => ({ ...current, positions: result.positions, versions: { ...current.versions, positions: version ?? current.versions.positions ?? "" } }));
    } else if (scope === "orders") {
      const result = await apiGet<{ orders: OrderRow[] }>("/api/orders", activeSession);
      setData((current) => ({ ...current, orders: result.orders, versions: { ...current.versions, orders: version ?? current.versions.orders ?? "" } }));
    } else if (scope === "brokerages") {
      const result = await apiGet<{ brokerages: BrokerageRow[] }>("/api/brokerages", activeSession);
      setData((current) => ({ ...current, brokerages: result.brokerages, versions: { ...current.versions, brokerages: version ?? current.versions.brokerages ?? "" } }));
    } else if (scope === "market") {
      const result = await apiGet<{ markets: MarketStripRow[] }>("/api/market/strip", activeSession);
      setData((current) => ({ ...current, marketStrip: result.markets, versions: { ...current.versions, market: version ?? current.versions.market ?? "" } }));
    } else if (scope === "subscriptions") {
      setData((current) => ({ ...current, versions: { ...current.versions, subscriptions: version ?? current.versions.subscriptions ?? "" } }));
    } else if (scope === "equity") {
      const result = await apiGet<{ range: string; points: EquityPoint[]; summary: EquitySummary | null }>("/api/equity/history?range=1M", activeSession);
      setData((current) => ({ ...current, equity: result, versions: { ...current.versions, equity: version ?? current.versions.equity ?? "" } }));
    } else if (scope === "news") {
      const result = await apiGet<{ news: NewsRow[] }>("/api/news", activeSession);
      setData((current) => ({ ...current, news: result.news, versions: { ...current.versions, news: version ?? current.versions.news ?? "" } }));
    } else if (scope === "movers") {
      const result = await apiGet<{ movers: MoverRow[] }>("/api/movers", activeSession);
      setData((current) => ({ ...current, movers: result.movers, versions: { ...current.versions, movers: version ?? current.versions.movers ?? "" } }));
    } else if (scope === "pl") {
      const now = new Date();
      const [monthly, summary] = await Promise.all([
        apiGet<{ days: CalendarDay[] }>(`/api/pl/monthly?year=${now.getFullYear()}&month=${now.getMonth() + 1}`, activeSession),
        apiGet<{ periods: PlSummaryPeriod[] }>("/api/pl/summary", activeSession)
      ]);
      setData((current) => ({
        ...current,
        pl: { days: monthly.days, summary: summary.periods },
        versions: { ...current.versions, pl: version ?? current.versions.pl ?? "" }
      }));
    }
  }

  function addBrokerage(brokerage: BrokerageRow) {
    setData((current) => ({
      ...current,
      brokerages: [brokerage, ...current.brokerages.filter((item) => item.id !== brokerage.id)],
      versions: {
        ...current.versions,
        brokerages: String(Date.now())
      }
    }));
  }

  async function refresh() {
    setIsLoading(true);
    setError(null);
    try {
      await loadData(await ensureFreshSession());
    } catch (caught) {
      try {
        const nextSession = await ensureFreshSession(true);
        await loadData(nextSession);
      } catch (refreshError) {
        if (caught instanceof ApiError && caught.status === 401) {
          onLogout();
          return;
        }
        if (refreshError instanceof Error && refreshError.message.includes("401")) {
          onLogout();
          return;
        }
        setError(caught instanceof Error ? caught.message : "Refresh failed");
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function logout() {
    await fetch(`${API_ORIGIN}/api/auth/logout`, {
      method: "POST",
      headers: authHeaders(sessionRef.current)
    }).catch(() => undefined);
    onLogout();
  }

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    let disposed = false;
    let socket: WebSocket | null = null;
    let heartbeatInterval: number | null = null;
    let heartbeatTimeout: number | null = null;
    let reconnectTimeout: number | null = null;

    function clearHeartbeat() {
      if (heartbeatInterval !== null) {
        window.clearInterval(heartbeatInterval);
        heartbeatInterval = null;
      }
      if (heartbeatTimeout !== null) {
        window.clearTimeout(heartbeatTimeout);
        heartbeatTimeout = null;
      }
    }

    function clearReconnect() {
      if (reconnectTimeout !== null) {
        window.clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
    }

    function closeSocket(reason: string) {
      const active = socket;
      socket = null;
      if (active && active.readyState !== WebSocket.CLOSED && active.readyState !== WebSocket.CLOSING) {
        active.close(1000, reason);
      }
    }

    function scheduleReconnect() {
      if (disposed || reconnectTimeout !== null) return;
      setStreamStatus("reconnecting");
      const attempt = realtimeReconnectAttemptRef.current;
      const baseDelay = Math.min(REALTIME_RECONNECT_MAX_MS, 1_000 * 2 ** attempt);
      const jitter = Math.floor(Math.random() * 500);
      realtimeReconnectAttemptRef.current = Math.min(attempt + 1, 5);
      reconnectTimeout = window.setTimeout(() => {
        reconnectTimeout = null;
        void connect();
      }, baseDelay + jitter);
    }

    function ping() {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: "ping", at: new Date().toISOString() }));
      if (heartbeatTimeout !== null) window.clearTimeout(heartbeatTimeout);
      heartbeatTimeout = window.setTimeout(() => {
        closeSocket("heartbeat_timeout");
        scheduleReconnect();
      }, REALTIME_HEARTBEAT_TIMEOUT_MS);
    }

    function markPong() {
      if (heartbeatTimeout !== null) {
        window.clearTimeout(heartbeatTimeout);
        heartbeatTimeout = null;
      }
    }

    function startHeartbeat() {
      clearHeartbeat();
      ping();
      heartbeatInterval = window.setInterval(ping, REALTIME_HEARTBEAT_MS);
    }

    async function connect() {
      if (disposed) return;
      clearReconnect();
      clearHeartbeat();
      closeSocket("reconnect");
      setStreamStatus((current) => current === "live" ? current : "connecting");

      let activeSession: SessionState;
      try {
        activeSession = await ensureFreshSession();
      } catch {
        if (!disposed) onLogout();
        return;
      }
      if (disposed) return;

      const nextSocket = new WebSocket(realtimeUrl(), ["traderviz", `jwt.${activeSession.access_token}`]);
      socket = nextSocket;

      nextSocket.addEventListener("open", () => {
        realtimeReconnectAttemptRef.current = 0;
        setStreamStatus("live");
        startHeartbeat();
      });

      nextSocket.addEventListener("message", (event) => {
        const message = readJson<RealtimeMessage>(String(event.data));
        if (message?.type === "connected" || message?.type === "pong") {
          if (message.type === "pong") markPong();
          setStreamStatus("live");
        }
        if (message?.scope && message.type !== "pong" && message.type !== "connected") {
          const payload = message.payload as RealtimePayload | undefined;
          if (message.scope === "market" && payload?.kind === "market_strip" && Array.isArray(payload.markets)) {
            setData((current) => ({
              ...current,
              marketStrip: mergeMarketStrip(current.marketStrip, payload.markets ?? []),
              versions: { ...current.versions, market: message.version ?? current.versions.market ?? "" }
            }));
            return;
          }
          if (message.scope === "subscriptions" && payload?.gateway_status) {
            setData((current) => ({
              ...current,
              gatewayStatus: payload.gateway_status ?? current.gatewayStatus,
              versions: { ...current.versions, subscriptions: message.version ?? current.versions.subscriptions ?? "" }
            }));
            return;
          }
          void refreshScope(message.scope, message.version).catch((caught) => {
            setError(caught instanceof Error ? caught.message : "Realtime refresh failed");
          });
        }
      });

      nextSocket.addEventListener("close", () => {
        if (socket === nextSocket) socket = null;
        clearHeartbeat();
        if (!disposed) scheduleReconnect();
      });

      nextSocket.addEventListener("error", () => {
        if (socket === nextSocket) {
          closeSocket("websocket_error");
        }
      });
    }

    function onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        scheduleReconnect();
      } else {
        ping();
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    void connect();

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearReconnect();
      clearHeartbeat();
      closeSocket("view_disposed");
    };
  }, [ensureFreshSession, onLogout]);

  return (
    <div className={`app-frame ${privacyMode ? "privacy-on" : ""}`}>
      <TopBar
        view={view}
        setView={navigate}
        session={session}
        streamStatus={streamStatus}
        gatewayStatus={data.gatewayStatus}
        market={market}
        theme={theme}
        privacyMode={privacyMode}
        onToggleTheme={() => setTheme((value) => value === "light" ? "dark" : "light")}
        onTogglePrivacy={() => setPrivacyMode((value) => !value)}
        onLogout={logout}
      />
      {error ? <div className="mv-page-pad"><div className="login-error">{error}</div></div> : null}
      {isLoading && data.accounts.length === 0 ? (
        <div className="mv-page-pad"><div className="mv-card loading-card">Loading tenant projections…</div></div>
      ) : null}
      {view === "overview" ? <OverviewPage data={data} /> : null}
      {view === "positions" ? <PositionsPage data={data} /> : null}
      {view === "strategies" ? <StrategiesPage /> : null}
      {view === "calendar" ? <CalendarPage getSession={ensureFreshSession} initialDays={data.pl.days} summary={data.pl.summary} /> : null}
      {view === "admin" ? <AdminPage data={data} getSession={ensureFreshSession} session={session} onBrokerageCreated={addBrokerage} /> : null}
    </div>
  );
}

function App() {
  const [session, setSession] = useState<SessionState | null>(() => {
    const stored = localStorage.getItem(SESSION_KEY);
    return stored ? readJson<SessionState>(stored) : null;
  });

  if (!session) return <Login onLogin={setSession} />;

  return (
    <Console
      session={session}
      onSession={setSession}
      onLogout={() => {
        localStorage.removeItem(SESSION_KEY);
        setSession(null);
      }}
    />
  );
}

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);
